/**
 * PATH: packages/email-hub/src/feature/action-signals/InvoiceNumberSignal.tsx
 * FILE_ID: EMAILHUB-ACTIONSIGNALS-INVOICE-001
 * PURPOSE: Invoice-number badge — copy, and (when the host wires it) open
 *          structured bill line items from the expanded INTEL lane.
 * WHY: Deterministic invoice# extractor (I02) needs a one-gesture copy
 *      surface; operator 2026-08-05 also needs the bill structure from
 *      expansion, not only a buried thread-footer CTA.
 * RELATED: types.ts, StatementViewer.tsx, IntelligenceLane.tsx
 */
import { useState } from 'react'
import { Check, ClipboardCopy, FileText } from 'lucide-react'

import { useToast } from '../../_internal/components/Toast'
import { cn } from '../../_internal/lib/utils'
import { writeCodeToClipboard } from './api'
import type { EmailActionSignal } from './types'

interface InvoiceNumberSignalProps {
  signal: EmailActionSignal
  /** When set, the invoice number opens structured bill/statement view. */
  onOpenStructure?: () => void
}

export function InvoiceNumberChip({
  signal,
  onOpenStructure,
}: InvoiceNumberSignalProps) {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await writeCodeToClipboard(signal.display_value)
      setCopied(true)
      toast('Copied invoice number', 'success')
    } catch {
      toast("Couldn't copy — your browser blocked clipboard access.", 'error')
    }
  }

  return (
    <span
      data-testid="invoice-number-chip"
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-sky-500/40',
        'bg-sky-500/10 px-1.5 py-0.5 text-[11px] text-sky-800 dark:text-sky-200',
      )}
      title={signal.context}
    >
      <FileText className="h-3 w-3 shrink-0" aria-hidden />
      {onOpenStructure ? (
        <button
          type="button"
          onClick={onOpenStructure}
          data-testid="invoice-number-open-structure"
          className="font-mono font-semibold tabular-nums underline-offset-2 hover:underline"
          title="Open bill structure (line items and totals)"
        >
          {signal.display_value}
        </button>
      ) : (
        <span className="font-mono font-semibold tabular-nums">
          {signal.display_value}
        </span>
      )}
      <button
        type="button"
        onClick={() => void copy()}
        data-testid="invoice-number-copy"
        className="inline-flex min-h-6 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold hover:bg-sky-500/20"
        title="Copy invoice number"
      >
        {copied ? <Check className="h-3 w-3" aria-hidden /> : <ClipboardCopy className="h-3 w-3" aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      {onOpenStructure && (
        <button
          type="button"
          onClick={onOpenStructure}
          data-testid="invoice-number-structure"
          className="inline-flex min-h-6 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 hover:bg-sky-500/20 dark:text-sky-200"
          title="Open bill structure (line items and totals)"
        >
          Structure
        </button>
      )}
    </span>
  )
}
